// frontend/src/utils/queryBuilder.js
// Query string generation utilities for search UI

/**
 * Build NEAR query string from state
 *
 * @param {Object} nearState - { keyword1, keyword2, distance }
 * @returns {string} NEAR query string or empty string
 *
 * @example
 * buildNearQuery({ keyword1: 'buddha', keyword2: 'dharma', distance: 10 })
 * // Returns: "buddha NEAR[10] dharma"
 */
export function buildNearQuery(nearState) {
  const { keyword1, keyword2, distance = 10 } = nearState || {};

  if (!keyword1?.trim() || !keyword2?.trim()) {
    return '';
  }

  return `${keyword1.trim()} NEAR[${distance}] ${keyword2.trim()}`;
}

/**
 * Build advanced query string from conditions array
 *
 * @param {Array} conditions - Array of condition objects
 * @param {string} conditions[].operator - 'AND' | 'OR' | 'NOT'
 * @param {string} conditions[].keyword - Search keyword
 * @returns {string} Boolean query string or empty string
 *
 * @example
 * buildAdvancedQuery([
 *   { id: 1, operator: 'AND', keyword: 'buddha' },
 *   { id: 2, operator: 'OR', keyword: 'dharma' },
 *   { id: 3, operator: 'NOT', keyword: 'tantra' }
 * ])
 * // Returns: "buddha / dharma NOT tantra"
 */
export function buildAdvancedQuery(conditions) {
  if (!Array.isArray(conditions) || conditions.length === 0) {
    return '';
  }

  // Filter out empty conditions
  const validConditions = conditions.filter((c) => c.keyword && c.keyword.trim());

  if (validConditions.length === 0) {
    return '';
  }

  // First condition has no operator prefix
  let query = validConditions[0].keyword.trim();

  // Subsequent conditions need operator
  for (let i = 1; i < validConditions.length; i++) {
    const { operator, keyword } = validConditions[i];
    const op = mapOperator(operator);
    query += ` ${op} ${keyword.trim()}`;
  }

  return query;
}

/**
 * Map UI operator to query syntax
 *
 * @param {string} operator - 'AND' | 'OR' | 'NOT'
 * @returns {string} Query operator
 */
function mapOperator(operator) {
  switch (operator) {
    case 'AND':
      return '+';
    case 'OR':
      return '/';
    case 'NOT':
      return 'NOT';
    default:
      return '+';
  }
}

/**
 * Generate unique ID for condition rows
 *
 * @returns {number} Unique ID based on timestamp
 */
export function generateConditionId() {
  return Date.now() + Math.random();
}

/**
 * Create initial condition object
 *
 * @param {string} operator - Initial operator (default: 'AND')
 * @param {string} field - Field selector: 'title' | 'content' | 'all' (default: 'all')
 * @returns {Object} Condition object { id, operator, keyword, field }
 */
export function createCondition(operator = 'AND', field = 'all') {
  return {
    id: generateConditionId(),
    operator,
    keyword: '',
    field,
  };
}

/**
 * Serialize UI conditions to API payload format.
 *
 * Strips internal `id` field, trims keywords, filters empty keywords, and
 * forces the first remaining condition's operator to empty string (backend
 * `_validateConditions` requires `condition[0].operator === ''`).
 *
 * @param {Array} conditions - UI condition objects with { id, operator, keyword, field }
 * @returns {Array<{keyword:string, operator:string, field:string}>} API-ready array
 *
 * @example
 * serializeConditionsForApi([
 *   { id: 1, operator: 'AND', keyword: 'buddha', field: 'title' },
 *   { id: 2, operator: 'OR',  keyword: 'dharma', field: 'content' },
 * ])
 * // [
 * //   { keyword: 'buddha', operator: '',   field: 'title' },
 * //   { keyword: 'dharma', operator: 'OR', field: 'content' },
 * // ]
 */
export function serializeConditionsForApi(conditions) {
  if (!Array.isArray(conditions)) {
    return [];
  }

  const result = [];
  for (const c of conditions) {
    if (!c || typeof c.keyword !== 'string') continue;
    const trimmed = c.keyword.trim();
    if (trimmed.length === 0) continue;
    result.push({
      keyword: trimmed,
      operator: c.operator,
      field: c.field || 'all',
    });
  }

  if (result.length > 0) {
    result[0] = { ...result[0], operator: '' };
  }

  return result;
}

/**
 * Validate query string for common issues
 *
 * @param {string} query - Query string to validate
 * @returns {Object} { valid: boolean, message?: string }
 */
export function validateQuery(query) {
  if (!query || !query.trim()) {
    return { valid: false, message: 'Query cannot be empty' };
  }

  // Check for unmatched brackets
  const openBrackets = (query.match(/\[/g) || []).length;
  const closeBrackets = (query.match(/\]/g) || []).length;
  if (openBrackets !== closeBrackets) {
    return { valid: false, message: 'Unmatched brackets in query' };
  }

  // Check for lonely operators
  const startsWithOp = /^(\+|\/|NOT\s)/i.test(query.trim());
  if (startsWithOp) {
    return { valid: false, message: 'Query cannot start with an operator' };
  }

  const endsWithOp = /(\+|\/|\sNOT)$/i.test(query.trim());
  if (endsWithOp) {
    return { valid: false, message: 'Query cannot end with an operator' };
  }

  return { valid: true };
}
